/* eslint-disable no-return-assign */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Icon, Notification, Typography } from '@telus-uds/components-web';
import LockClosed from '@telus-uds/palette-allium/build/web/icons/LockClosed';

import { addLocale } from '../misc';
import { useLocaleContext } from '../context/LocaleContext';
import { Section, Form, InputContainer, Input } from './css';
import request from '../utils/request';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const TwoFactorAuthentication = ({ history }) => {
  const locale = useLocaleContext();
  const addLocaleToUrl = addLocale(locale);
  const userEmail = localStorage.getItem('userEmail');
  const {
    '2fa': {
      label2fa,
      labelAuthenticationCode,
      noteAuthCode,
      labelVerify,
      labelVerifying,
      labelDidntReceiveEmail,
      labelResendCode,
      labelResendCodeIn,
      labelSec,
      authenticationCodeResendSuccessMessage,
      invalidCodeErrorMessage,
      codeExpiredErrorMessage
    }
  } = useLocalizedStringsContext();

  const RESET_TIME = 30;
  const ERROR_CODE_MAPPING = {
    INVALID_CODE: invalidCodeErrorMessage,
    CODE_EXPIRED: codeExpiredErrorMessage
  };

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [canVerify, setCanVerify] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendOtpSuccess, setResendOtpSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(RESET_TIME);
  const [timerActive, setTimerActive] = useState(false);
  const inputRefs = useRef([]);

  const handleChange = (index, event) => {
    if (error) setError(false);
    if (resendOtpSuccess) setResendOtpSuccess(false);

    const { value } = event.target;
    if (/^\d{0,1}$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && index > 0) {
      const newCode = [...code];
      newCode[index] = '';
      setCode(newCode);
      inputRefs.current[index - 1].focus();
    }
  };

  const getUserProfile = () => {
    return request.get('/users/profile');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      setVerifying(true);
      try {
        const response = await request.post('/users/login', {
          email: userEmail,
          code: fullCode
        });

        const { changePassword, token } = response && response.data;
        localStorage.setItem('isExpired', 'false');
        request.setToken(token);
        request.setPasswordUpdate(changePassword);
        const userProfile = await getUserProfile();

        request.setProfile(userProfile.data);
        history.push(addLocaleToUrl('/'));
        window.location.reload();
      } catch (err) {
        const errorCode = _.get(err, 'response.data.error', '');
        setVerifying(false);
        setError(errorCode);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0].focus();
      }
    }
  };

  const resendCode = async () => {
    setTimerActive(true);
    setResendOtpSuccess(false);
    try {
      const response = await request.post('/users/login', {
        email: userEmail,
        code: 'resend'
      });
      if (response && response.status === 200) {
        setResendOtpSuccess(true);
        setTimerActive(true);
      }
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    let interval;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prevTime => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
      setTimeLeft(RESET_TIME);
      setTimerActive(false);
    }

    return () => {
      clearInterval(interval);
    };
  }, [timerActive, timeLeft]);

  useEffect(() => {
    inputRefs.current[0].focus();
  }, []);

  useEffect(() => {
    const allDigitsEntered = code.every(digit => !!digit);
    setCanVerify(allDigitsEntered);
  }, [code]);

  return (
    <Section>
      <Typography heading="h2" variant={{ size: 'h2' }} block>
        {label2fa}
      </Typography>
      <Form onSubmit={handleSubmit}>
        <Icon icon={LockClosed} variant={{ size: 'large' }} accessibilityLabel="Two-Factor Authentication" />
        <Typography heading="h3" variant={{ size: 'h3' }} block>{labelAuthenticationCode}</Typography>
        <Typography>{noteAuthCode}</Typography>
        <InputContainer>
          {code.map((digit, index) => (
            <Input
              key={index}
              ref={input => (inputRefs.current[index] = input)}
              type="text"
              value={digit}
              onChange={event => handleChange(index, event)}
              onKeyDown={event => handleKeyDown(index, event)}
              maxLength={1}
            />
          ))}
        </InputContainer>
        <Button
          type="submit"
          variant={{ priority: 'high' }}
          disabled={!canVerify || verifying}
          onPress={handleSubmit}
        >
          {verifying ? labelVerifying : labelVerify}
        </Button>
        {error && (
          <Notification
            variant={{ validation: 'error' }}
            dismissible
            copy="en"
            onDismiss={() => setError(false)}
          >
            <Typography>
              {_.get(ERROR_CODE_MAPPING, error, invalidCodeErrorMessage)}
            </Typography>
          </Notification>
        )}
      </Form>

      <Typography>
        {labelDidntReceiveEmail}
        {timerActive ? (
          <Typography>
            {labelResendCodeIn}{' '}
            <Typography variant={{ bold: true }}>
              {timeLeft} {labelSec}
            </Typography>
          </Typography>
        ) : (
          <Button variant={{ text: true }} onPress={resendCode}>
            <Typography variant={{ bold: true }}>{labelResendCode}</Typography>
          </Button>
        )}
      </Typography>
      {resendOtpSuccess && (
        <Notification
          variant={{ style: 'success' }}
          dismissible
          copy="en"
          onDismiss={() => setResendOtpSuccess(false)}
        >
          <Typography>{authenticationCodeResendSuccessMessage}</Typography>
        </Notification>
      )}
    </Section>
  );
};

TwoFactorAuthentication.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default TwoFactorAuthentication;
